import {
  Injectable,
  inject,
  signal,
  computed,
  DestroyRef
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Observable, EMPTY, forkJoin, combineLatest } from 'rxjs';
import {
  switchMap,
  catchError,
  tap,
  map
} from 'rxjs/operators';
import { LoggerService } from '@core/services/logger.service';
import { AuthService } from '@core/services/auth.service';
import { BrowserService } from '@core/services/browser.service';
import { TenantStore } from '@stores/tenant.store';
import { ExchangeService, Exchange } from '../../exchange-management/services/exchange.service';

// Generated OpenAPI Services
import {
  ExchangeApiService,
} from '@api/api/exchange.service';
import { HftMetricsApiService } from '@api/api/hftMetrics.service';
import { SystemApiService } from '@api/api/system.service';
import {
  ReconnectExchangeResponse as GeneratedReconnectExchangeResponse,
} from '@api/model/models';

/**
 * Per-exchange connection status.
 */
export interface ExchangeConnectionState {
  exchangeId: string;
  name: string;
  status: 'connected' | 'disconnected' | 'degraded' | 'connecting' | 'error' | 'unknown';
  latencyMs: number;
  throughput: number;
  uptime: number;
  lastConnected: Date | null;
  protocols: string[];
  region: string;
  errorCount: number;
  orderSuccessRate: number;
}

/**
 * Exchange connectivity summary.
 */
export interface ExchangeConnectivitySummary {
  totalExchanges: number;
  connectedCount: number;
  disconnectedCount: number;
  degradedCount: number;
  averageLatencyMs: number;
  totalThroughput: number;
  exchanges: ExchangeConnectionState[];
}

/**
 * Response from the exchange reconnect endpoint.
 */
export interface ReconnectExchangeResponse {
  healthy: boolean;
  message: string;
  exchange: string;
  latencyMs: number;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExchangeConnectivityService {
  private readonly logger = inject(LoggerService);
  private readonly systemApi = inject(SystemApiService);
  private readonly exchangeApi = inject(ExchangeApiService);
  private readonly hftMetrics = inject(HftMetricsApiService);
  private readonly authService = inject(AuthService);
  private readonly credentials = inject(ExchangeService);
  private readonly tenants = inject(TenantStore);
  private readonly destroyRef = inject(DestroyRef);
  /** RF-QA-171 · visibility-aware poll clock (pauses while the tab is hidden). */
  private readonly browser = inject(BrowserService);
  private readonly scope = computed(() => {
    const user = this.authService.currentUser();
    const tenant = this.tenants.currentTenant()?.id ?? user?.tenantId;
    return user?.id && tenant ? `${user.id}:${tenant}` : null;
  });

  // Reactive state via signals
  private readonly _summary = signal<ExchangeConnectivitySummary | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _lastRefreshed = signal<Date | null>(null);

  // Public readonly signals
  readonly summary = this._summary.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly lastRefreshed = this._lastRefreshed.asReadonly();

  // Computed signals
  readonly connectivityPercentage = computed(() => {
    const s = this._summary();
    if (!s || s.totalExchanges === 0) return 0;
    return Math.round((s.connectedCount / s.totalExchanges) * 100);
  });

  readonly overallStatus = computed<'healthy' | 'degraded' | 'critical' | 'unknown'>(() => {
    const s = this._summary();
    if (!s || !s.totalExchanges) return 'unknown';
    if (s.disconnectedCount > 0) return 'critical';
    if (s.degradedCount > 0) return 'degraded';
    if (s.exchanges.some(exchange => exchange.status !== 'connected')) return 'unknown';
    return 'healthy';
  });

  readonly exchangeList = computed(() => {
    return this._summary()?.exchanges ?? [];
  });

  constructor() {
    this.startPeriodicRefresh();
  }

  /**
   * Load exchange connectivity data from the trading-health overview endpoint.
   */
  loadExchangeConnectivity(): Observable<ExchangeConnectivitySummary> {
    const scope = this.scope();
    this._loading.set(true);
    this._error.set(null);

    return forkJoin({
      overview: this.systemApi.tradingHealthOverview() as unknown as Observable<Record<string, unknown>>,
      credentials: this.credentials.listExchanges(),
    }).pipe(
      map(({ overview, credentials }) => this.mapToConnectivitySummary(overview, credentials)),
      tap((summary) => {
        if (scope !== this.scope()) return;
        this._summary.set(summary);
        this._lastRefreshed.set(new Date());
        this._loading.set(false);
      }),
      catchError((err) => {
        if (scope !== this.scope()) throw err;
        this._loading.set(false);
        // A request that races ahead of auth/tenant context is rejected by the
        // tenant interceptor with a benign 400 "No tenant context" (see
        // isNoTenantContextError). This is an expected pre-auth condition, not a
        // failure: skip it quietly (debug, no Sentry event) and surface no error
        // banner. The startPeriodicRefresh gate below normally prevents the call
        // from firing pre-auth at all; this is defence-in-depth for the race.
        if (this.isNoTenantContextError(err)) {
          this.logger.debug('ExchangeConnectivityService: skipped pre-auth load (no tenant context)');
          this._error.set(null);
          throw err;
        }
        this._summary.set(null);
        const message = 'Exchange connectivity data is unavailable.';
        this.logger.error('ExchangeConnectivityService: load failed', { error: err });
        this._error.set(message);
        throw err;
      })
    );
  }

  /**
   * Get connectivity data for a specific exchange.
   */
  getExchangeStatus(exchangeId: string): ExchangeConnectionState | undefined {
    return this._summary()?.exchanges.find(e => e.exchangeId === exchangeId);
  }

  /**
   * Trigger a reconnection attempt for an exchange.
   * Returns an observable with the reconnection result.
   */
  reconnectExchange(exchangeId: string): Observable<ReconnectExchangeResponse> {
    return (this.exchangeApi.reconnectExchange(exchangeId) as unknown as Observable<GeneratedReconnectExchangeResponse>).pipe(
      map((result) => result as ReconnectExchangeResponse),
      tap((result) => {
        this.logger.info('Exchange reconnect result', { exchange: exchangeId, result });
      }),
      catchError((err) => {
        this.logger.error('Exchange reconnect failed', { exchange: exchangeId, error: err });
        throw err;
      })
    );
  }

  /**
   * Force a manual refresh.
   */
  refresh(): void {
    this.loadExchangeConnectivity()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          // Benign pre-auth / tenant-switch race — skip quietly (see catchError
          // in loadExchangeConnectivity). Everything else is a real failure.
          if (this.isNoTenantContextError(err)) {
            this.logger.debug('ExchangeConnectivityService: manual refresh skipped (no tenant context)');
            return;
          }
          this.logger.error('ExchangeConnectivityService: manual refresh failed', { error: err });
        }
      });
  }

  private mapToConnectivitySummary(data: Record<string, unknown>, credentials: Exchange[]): ExchangeConnectivitySummary {
    const exchangesData = (data['exchanges'] || {}) as Record<string, unknown>;
    const requestStats = (data['request_stats'] || {}) as Record<string, unknown>;
    const statuses = (exchangesData['statuses'] || {}) as Record<string, string>;
    // Optional per-exchange detail block keyed by exchange name. When the
    // backend supplies it, protocols/region are read from here; otherwise both
    // are reported as not-available (see getExchangeProtocols/getExchangeRegion)
    // rather than fabricated from a hardcoded venue lookup table.
    const details = (exchangesData['details'] || {}) as Record<string, unknown>;

    // The /trading-health/overview endpoint exposes a per-exchange connection
    // *status* string plus a single SERVER-WIDE request_stats block (gateway
    // totals + average latency). It does NOT expose per-exchange uptime,
    // latency, throughput, error counts or order-success rates. Attributing the
    // aggregate gateway figures to each venue — or inventing uptime from the
    // status enum — produced fabricated per-venue telemetry (GAP-030), so those
    // per-exchange metrics are reported as unavailable (-1) rather than guessed.
    // The status string remains real and is the basis of connected/degraded
    // counts. (-1 is the agreed "not available" sentinel; 0 is a real value.)
    const UNAVAILABLE = -1;

    const configuredNames = [...new Set(credentials.map(exchange => exchange.name.toLowerCase()))];
    const exchanges: ExchangeConnectionState[] = configuredNames.map(name => {
      const statusStr = Object.entries(statuses).find(([key]) => key.toLowerCase() === name)?.[1];
      const active = credentials.some(exchange => exchange.name.toLowerCase() === name && exchange.isActive);
      return {
        exchangeId: name.toLowerCase(),
        name: this.formatExchangeName(name),
        status: active ? this.mapStatus(statusStr ?? '') : 'disconnected',
        // Per-exchange telemetry is not provided by the overview endpoint.
        latencyMs: UNAVAILABLE,
        throughput: UNAVAILABLE,
        uptime: UNAVAILABLE,
        lastConnected: null,
        protocols: this.getExchangeProtocols(details[name]),
        region: this.getExchangeRegion(details[name]),
        errorCount: UNAVAILABLE,
        orderSuccessRate: UNAVAILABLE
      };
    });

    const connectedCount = exchanges.filter(e => e.status === 'connected').length;
    const disconnectedCount = exchanges.filter(e => e.status === 'disconnected' || e.status === 'error').length;
    const degradedCount = exchanges.filter(e => e.status === 'degraded').length;

    // Summary-level latency/throughput are sourced from the real, server-wide
    // request_stats block (gateway average latency + total request count). These
    // are genuine measured aggregates — unlike the per-exchange breakdown, which
    // the backend does not expose. A negative/missing value is reported as -1
    // (unavailable) rather than a fabricated default.
    const avgLatencyRaw = requestStats['avg_latency_ms'];
    const averageLatencyMs = typeof avgLatencyRaw === 'number' && avgLatencyRaw >= 0
      ? avgLatencyRaw
      : -1;

    const totalRequests = requestStats['total'];
    const totalThroughput = typeof totalRequests === 'number' && totalRequests >= 0
      ? Math.floor(totalRequests / 60)
      : -1;

    return {
      totalExchanges: exchanges.length,
      connectedCount,
      disconnectedCount,
      degradedCount,
      averageLatencyMs,
      totalThroughput,
      exchanges
    };
  }

  private formatExchangeName(name: string): string {
    const names: Record<string, string> = {
      'binance': 'Binance',
      'coinbase': 'Coinbase Pro',
      'kraken': 'Kraken',
      'ftx': 'FTX',
      'huobi': 'Huobi',
      'okx': 'OKX',
      'bybit': 'Bybit',
      'kucoin': 'KuCoin'
    };
    return names[name.toLowerCase()] || name.charAt(0).toUpperCase() + name.slice(1);
  }

  private mapStatus(statusStr: string): ExchangeConnectionState['status'] {
    switch (statusStr?.toLowerCase()) {
      case 'connected':
      case 'active':
      case 'operational':
        return 'connected';
      case 'degraded':
      case 'warning':
        return 'degraded';
      case 'connecting':
        return 'connecting';
      case 'disconnected':
      case 'offline':
        return 'disconnected';
      case 'error':
      case 'failed':
        return 'error';
      default:
        return 'unknown';
    }
  }

  /**
   * Reads the supported protocols for an exchange from the backend per-exchange
   * detail block. The previous implementation returned a hardcoded venue→protocol
   * lookup table (GAP-027), fabricating badges the server never reported. When the
   * backend does not supply `protocols`, an empty list is returned so the UI shows
   * no protocol badges rather than invented ones.
   */
  private getExchangeProtocols(detail: unknown): string[] {
    const protocols = (detail as Record<string, unknown> | undefined)?.['protocols'];
    if (Array.isArray(protocols)) {
      return protocols.filter((p): p is string => typeof p === 'string');
    }
    return [];
  }

  /**
   * Reads the region for an exchange from the backend per-exchange detail block.
   * The previous implementation returned a hardcoded venue→region lookup table
   * (GAP-027). When the backend does not supply `region`, 'Unknown' is returned
   * to signal not-available rather than guessing a region.
   */
  private getExchangeRegion(detail: unknown): string {
    const region = (detail as Record<string, unknown> | undefined)?.['region'];
    return typeof region === 'string' && region.trim().length > 0 ? region : 'Unknown';
  }

  private startPeriodicRefresh(): void {
    // GAP ADMIN-INTERFACE-6S/6T — this root singleton is constructed as soon as
    // any consumer (e.g. the always-present navbar / global-websocket-status)
    // injects it, which happens while still on the /auth/login route. The poll
    // below is TENANT-SCOPED (GET /trading-health/overview); firing it pre-auth
    // is rejected by the tenant interceptor with a 400 "No tenant context",
    // which previously logged at error level on every tick -> Sentry spam.
    //
    // Mirror the GAP #23 pattern in RiskManagementService: gate on
    // authService.isAuthenticated$ so neither the initial load NOR the 15s
    // refresh fires until auth + tenant context exist. switchMap tears the inner
    // timer down on logout (isAuthenticated$ -> false), so we never poll on the
    // login screen. An authenticated session (re)starts the timer(0, 15000).
    //
    // RF-QA-171: the 15 s clock is BrowserService.visibleTimer — the same
    // cadence, but it pauses while the tab is hidden and fires immediately
    // when the tab becomes visible again. This root singleton is the single
    // owner of the GET /trading-health/overview + /exchanges/credentials pair;
    // every consumer reads its signals, so N consumers still cost one request.
    const scope = toObservable(this.scope);
    combineLatest([this.authService.isAuthenticated$, scope]).pipe(
      switchMap(([isAuth, tenant]) => {
        this._summary.set(null);
        this._lastRefreshed.set(null);
        return isAuth && tenant
          ? this.browser.visibleTimer(15000).pipe(
              switchMap(() => this.loadExchangeConnectivity().pipe(
                catchError((err) => {
                  // A late tick may still race a tenant switch / logout; treat the
                  // benign "No tenant context" 400 as a quiet skip (debug, no
                  // Sentry event) and keep the outer stream alive.
                  if (this.isNoTenantContextError(err)) {
                    this.logger.debug('ExchangeConnectivityService: periodic refresh skipped (no tenant context)');
                  } else {
                    this.logger.error('ExchangeConnectivityService: periodic refresh failed', { error: err });
                  }
                  return EMPTY;
                })
              ))
            )
          : EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(); // No subscriber error handler needed: catchError inside switchMap handles errors
  }

  /**
   * True for the benign 400 the tenant interceptor raises when a tenant-scoped
   * request is attempted before auth/tenant context exists ("No tenant context
   * available..."). Such a request is an expected pre-auth condition — not a
   * failure — so it is logged at debug level (no error/warn -> no Sentry event)
   * rather than spamming the error channel from the login screen.
   */
  private isNoTenantContextError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse) || err.status !== 400) {
      return false;
    }
    const detail = (err.error as { message?: unknown } | null)?.message;
    return typeof detail === 'string' && detail.includes('No tenant context');
  }
}
